import React, { useState } from 'react';
import { ChevronLeft, Mail, ShoppingCart, AlertTriangle, ShieldCheck, Landmark } from 'lucide-react';
import quizContent from './content/quiz.json';
import logoImg from './cyber-wisdom-logo.jpg';

export default function App() {
  const [step, setStep] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);

  const currentQuestion = quizContent.questions[step];
  const progressPercent = ((step + 1) / quizContent.questions.length) * 100;

  const handleOptionClick = (idx: number) => {
    setSelectedOption(idx);
    // Add a slight delay so they see the selection change before moving
    setTimeout(() => {
      setStep(step + 1);
      setSelectedOption(null);
    }, 300);
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
      setSelectedOption(null);
    }
  };

  const questionIcons = [
    <Mail className="w-14 h-14 text-[#1b75ff]" strokeWidth={1.5} />,
    <Landmark className="w-14 h-14 text-[#1b75ff]" strokeWidth={1.5} />,
    <ShoppingCart className="w-14 h-14 text-[#1b75ff]" strokeWidth={1.5} />,
    <AlertTriangle className="w-14 h-14 text-[#1b75ff]" strokeWidth={1.5} />,
    <ShieldCheck className="w-14 h-14 text-[#1b75ff]" strokeWidth={1.5} />
  ];

  const QuizScreen = () => (
    <div className="flex flex-col min-h-screen bg-white font-sans max-w-lg mx-auto relative overflow-hidden">
      
      {/* Pinned Full-Width Progress Bar */}
      <div className="absolute top-0 left-0 w-full h-1.5 bg-[#f0f2f5] z-50">
        <div 
          className="h-full bg-[#1b75ff] transition-all duration-300"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Top Header with Back Button and Logo */}
      <div className="flex items-center justify-center px-6 pt-6 pb-2 relative mt-1.5">
        <button 
          onClick={handleBack} 
          className={`absolute left-6 w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-[0_2px_10px_rgba(0,0,0,0.08)] border border-gray-50 z-10 ${step === 0 ? 'opacity-0 pointer-events-none' : ''}`}
        >
          <ChevronLeft className="w-5 h-5 text-gray-700" />
        </button>
        {!window.location.href.toLowerCase().includes('privacy-quiz') && (
          <img src={logoImg} alt="Cyber Wisdom" className="h-20 w-20 object-contain rounded-full shadow-sm" />
        )}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col px-6 pt-4 pb-24 overflow-y-auto">
        
        {/* Category Tag & Icon */}
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="w-24 h-24 bg-[#eef5ff] rounded-3xl flex items-center justify-center mb-6 shadow-sm">
            {questionIcons[step]}
          </div>
          {currentQuestion.step && (
            <span className="bg-[#eef5ff] text-[#1b75ff] text-[14px] font-bold px-4 py-1.5 rounded-lg inline-block">
              {currentQuestion.step}
            </span>
          )}
        </div>

        {/* Question Title */}
        <h1 className="text-[28px] font-bold leading-tight text-gray-900 mb-8 text-center">
          {currentQuestion.question}
        </h1>

        {/* Options List */}
        <div className="space-y-3">
          {currentQuestion.options.map((option: string, idx: number) => {
            const isSelected = selectedOption === idx;
            return (
              <button 
                key={idx}
                onClick={() => handleOptionClick(idx)}
                className={`w-full text-left p-4 rounded-2xl transition-all flex items-center justify-between group ${
                  isSelected 
                    ? 'bg-[#eef5ff] ring-2 ring-[#1b75ff]' 
                    : 'bg-[#f4f6f8] hover:bg-[#ebf0f5]'
                }`}
              >
                <span className="text-[16px] font-semibold text-gray-900">{option}</span>
                <div className={`w-[22px] h-[22px] rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                  isSelected ? 'border-[#1b75ff] bg-[#1b75ff]' : 'border-[#d1d5db] bg-white'
                }`}>
                  {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="mt-auto p-6 bg-white">
      </div>
    </div>
  );

  const FinalScreen = () => {
    const [isLoading, setIsLoading] = useState(false);

    React.useEffect(() => {
      const handlePageShow = (event: PageTransitionEvent) => {
        if (event.persisted) {
          setIsLoading(false);
        }
      };
      window.addEventListener('pageshow', handlePageShow);
      return () => window.removeEventListener('pageshow', handlePageShow);
    }, []);

    // Generate the exit link based on TS tracking params or fallback to the default CTA with query params appended
    const getExitLink = (defaultUrl: string) => {
      const searchParams = new URLSearchParams(window.location.search);
      const hash = searchParams.get('hash');
      const sid1 = searchParams.get('sid1');
      const sid2 = searchParams.get('sid2');
      const sid3 = searchParams.get('sid3');
      const domain = searchParams.get('domain');

      // If all TS tracking params are present, construct the custom exit link
      if (hash && sid1 && sid2 && sid3 && domain) {
        if (domain.toLowerCase() === 'aura') {
          return `https://track.${domain}.com/${hash}/click/${sid1}/${sid2}/${sid3}`;
        }
        return `https://url.${domain}.com/${hash}/click/${sid1}/${sid2}/${sid3}`;
      }

      // Fallback: append existing query params to the default URL
      const queryParams = window.location.search;
      return defaultUrl.includes('?') 
        ? `${defaultUrl}${queryParams ? '&' + queryParams.substring(1) : ''}`
        : `${defaultUrl}${queryParams}`;
    };

    const finalCtaUrl = getExitLink(quizContent.cta_url);

    const handleCtaClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      setIsLoading(true);
      setTimeout(() => {
        window.location.href = finalCtaUrl;
      }, 2000); // 2 seconds loading screen
    };

    if (isLoading) {
      return (
        <div className="flex flex-col min-h-screen bg-[#f8f9fa] font-sans max-w-lg mx-auto relative overflow-hidden items-center justify-center p-6">
          <div className="bg-white rounded-3xl p-10 flex flex-col items-center justify-center w-full max-w-sm shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
            <div className="w-12 h-12 border-4 border-[#eef5ff] border-t-[#1b75ff] rounded-full animate-spin mb-6"></div>
            <h2 className="text-2xl font-bold mb-2 text-gray-900 text-center">Finding Best Offer...</h2>
            <p className="text-gray-500 text-center text-sm">Redirecting you to the best option available.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col min-h-screen bg-white font-sans max-w-lg mx-auto relative overflow-hidden">
        {/* Pinned Full-Width Progress Bar */}
        <div className="absolute top-0 left-0 w-full h-1.5 bg-[#f0f2f5] z-50">
          <div className="h-full bg-[#1b75ff] w-full" />
        </div>

        {/* Top Header with Back Button and Logo */}
        <div className="flex items-center justify-center px-6 pt-6 pb-2 relative mt-1.5">
          <button 
            onClick={handleBack} 
            className="absolute left-6 w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-[0_2px_10px_rgba(0,0,0,0.08)] border border-gray-50 z-10"
          >
            <ChevronLeft className="w-5 h-5 text-gray-700" />
          </button>
          {!window.location.href.toLowerCase().includes('privacy-quiz') && (
            <img src={logoImg} alt="Cyber Wisdom" className="h-20 w-20 object-contain rounded-full shadow-sm" />
          )}
        </div>

        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-20 h-20 bg-[#fff4f4] rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-10 h-10 text-[#ff3b3b]" strokeWidth={2.5} />
          </div>
          <h2 className="text-3xl font-extrabold mb-4 text-gray-900">{quizContent.final_title}</h2>
          <p className="text-gray-600 text-[17px] mb-8 max-w-sm">{quizContent.final_description}</p>
        </div>

        {/* Bottom Fixed Actions */}
        <div className="p-6 bg-white">
          <a 
            href={finalCtaUrl}
            onClick={handleCtaClick}
            className="w-full block text-center bg-[#1b75ff] active:bg-blue-700 text-white font-bold text-[17px] py-4 px-6 rounded-full transition-colors mb-6 shadow-[0_4px_14px_rgba(27,117,255,0.3)]"
          >
            {quizContent.cta_text}
          </a>
        </div>
      </div>
    );
  };

  return step < quizContent.questions.length ? <QuizScreen /> : <FinalScreen />;
}
